<script lang="ts" setup>
import { Tab, Tabs } from "vant";
import { ref, watch, computed, onMounted, nextTick } from "vue";
import { useRoute, useRouter } from "vue-router";
import SaveTraitModal from "./SaveTraitModal.vue";
import MoreActionModal from "./MoreActionModal.vue";
import ThreadsNav from "@/components/mobile/ThreadsNav.vue";
import CreateTaskGuide from "@/components/mobile/generation/CreateTaskGuide.vue";
import ThreadList, {
  ThreadListInstance,
} from "@/components/mobile/ThreadList.vue";
import { useTask } from "@/stores/task";
import { useI18n } from "vue-i18n";
import DetailModal from "./DetailModal.vue";
import Card from "./Card/index.vue";
import { routerBack } from "@/use/go";

const listRef = ref<ThreadListInstance | null>(null);

// Constants
const hashes: string[] = ["#unfinished", "#toMark", "#liked", "#disliked"];

const SubTab: TaskTab[] = [
  "All",
  "TextImage",
  "MateTrait",
  // "TextGif",
  // "StoryImage",
  // "StoryVideo",
];

// States
const taskStore = useTask();
const router = useRouter();
const showCreateTaskGuide = ref(false);

type TaskIdx = 0 | 1 | 2 | 3;
// Lifecycle
const route = useRoute();

watch(
  () => taskStore.list[1],
  async (unmarkedList) => {
    if (unmarkedList[taskStore.selectedSubType].length === 2)
      await taskStore.fetchTask();
    if (taskStore.currentList.length <= 2) await taskStore.fetchTask(true);
  },
  {
    deep: true,
  }
);

watch([() => taskStore.selectedType, () => taskStore.selectedSubType], () => {
  listRef.value?.scrollTo(0);
  taskStore.cacheScrollTop = 0;
  listRef.value?.resetScrollLoadingState();
});

const onTabChange = () => {
  listRef.value?.resetScrollLoadingState();
  router.replace({
    name: "imageGenerateList",
    hash: hashes[taskStore.selectedType],
  });
  if (
    taskStore.list[taskStore.selectedType][taskStore.selectedSubType].length > 0
  )
    taskStore.fetchTask(true);
};

const onSubTabChange = (item: TaskTab) => {
  taskStore.selectedSubType = item;
  if (
    taskStore.list[taskStore.selectedType][taskStore.selectedSubType].length > 0
  )
    taskStore.fetchTask(true);
  nextTick(() => listRef.value?.resetScrollLoadingState());
};

watch(
  () => route.hash,
  (hash) => {
    if (hash && hash !== hash[taskStore.selectedType]) {
      let idx = hashes.findIndex((item) => item === hash);
      if (idx === -1) idx = 0;
      taskStore.selectedType = idx as TaskIdx;
    }
  }
);

const { t } = useI18n();

const badge = computed(() => [
  taskStore.list[0].All.length,
  taskStore.unmarkedCount,
  0,
  0,
]);

onMounted(() => {
  taskStore.updateUnmarkedCount();
  if (hashes.includes(route.hash)) {
    taskStore.selectedType =
      (hashes.findIndex((item) => item === route.hash) as TaskIdx) || 0;
  }
  nextTick(() => {
    if (taskStore.cacheScrollTop)
      listRef.value?.scrollTo(taskStore.cacheScrollTop);
  });
});
</script>
<template>
  <div class="h-full grid grid-rows-[auto_1fr_auto]">
    <threads-nav
      :go-back="() => ((taskStore.cacheScrollTop = 0), routerBack())"
      show-back
    >
      <template #center>
        <div></div>
      </template>
      <template #right>
        <button
          class="btn btn-primary btn-xs rounded"
          @click="showCreateTaskGuide = true"
        >
          <!-- <i class="iconfont icon-a- font-light" /> -->
          {{ $t("Buttons.newTask") }}
        </button>
      </template>
    </threads-nav>
    <div class="w-full overflow-hidden grid grid-rows-[auto_auto_1fr]">
      <tabs
        v-model:active="taskStore.selectedType"
        swipeable
        @change="onTabChange"
      >
        <tab
          v-for="i in 4"
          :key="i"
          :badge="badge[i - 1]"
          :show-zero-badge="false"
          :title="t(`task.type.${i - 1}`)"
        >
        </tab>
      </tabs>
      <div class="mt-2 pb-1">
        <div
          class="px-2 flex overflow-x-auto whitespace-nowrap justify-center cursor-pointer"
        >
          <div
            v-for="(item, idx) in SubTab"
            :key="idx"
            :class="[
              'px-2 py-1 border border-r-0 last:border-r',
              taskStore.selectedSubType === item && 'bg-secondary text-white',
            ]"
            @click="onSubTabChange(item)"
          >
            {{ t(`task.tab.${item}`) }}
          </div>
        </div>
        <div
          v-if="taskStore.selectedType === 3"
          class="bg-error text-center text-white"
        >
          {{ $t(`task.deleteTip`) }}
        </div>
      </div>
      <thread-list
        ref="listRef"
        :on-pull-load="() => taskStore.fetchTask(true)"
        :on-scroll-load="taskStore.fetchTask"
        class="h-full"
        @on-scroll="(h) => (taskStore.cacheScrollTop = h)"
      >
        <div :class="`tab-list${taskStore.selectedType}`">
          <Card
            v-for="item in taskStore.currentList"
            :id="item"
            :key="String(item)"
          />
        </div>
      </thread-list>
    </div>

    <DetailModal />
    <SaveTraitModal />
    <MoreActionModal />
    <create-task-guide v-model:show="showCreateTaskGuide" />
  </div>
</template>

<style scoped>
.tab-list2,
.tab-list3 {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: 0.5rem;
}
</style>
